import { Emitter } from "@core/events/emitter";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ISkyboxModel } from "./SkyboxModel";

/**
 * `SkyboxImportModel` — port of Unity's `SkyboxImportModel`
 * (`Models/SceneModel/SkyboxImportModel.cs`), minus the desktop import
 * task queue: browser uploads are a single awaited S3 put, so the model
 * tracks one in-flight import per scene and emits the same
 * begin / completed / failed lifecycle the inspector binds to.
 */

/** Unity `SkyboxContextViewModel.k_fileExtensions`. */
export const SKYBOX_IMPORT_EXTENSIONS = ["jpg", "jpeg", "png", "hdr", "hdri"] as const;

export interface ISkyboxImportModel {
  readonly onBeginSkyboxImport: Emitter<{ sceneId: string }>;
  readonly onSkyboxImportCompleted: Emitter<{ sceneId: string }>;
  readonly onSkyboxImportFailed: Emitter<{ sceneId: string; error: Error }>;

  isImportingSkybox(sceneId: string): boolean;
  /** Upload a new custom skybox texture for the scene. */
  importSkybox(sceneId: string, file: File): Promise<void>;
  /** Remove the scene's custom skybox texture (Unity `ClearSkybox`). */
  clearSkybox(sceneId: string): Promise<void>;
}

export class SkyboxImportModel implements ISkyboxImportModel {
  readonly onBeginSkyboxImport = new Emitter<{ sceneId: string }>();
  readonly onSkyboxImportCompleted = new Emitter<{ sceneId: string }>();
  readonly onSkyboxImportFailed = new Emitter<{ sceneId: string; error: Error }>();

  private readonly m_projectModel: IProjectModel;
  private readonly m_skyboxModel: ISkyboxModel;
  private readonly m_importingSceneIds = new Set<string>();

  constructor(projectModel: IProjectModel, skyboxModel: ISkyboxModel) {
    this.m_projectModel = projectModel;
    this.m_skyboxModel = skyboxModel;
  }

  isImportingSkybox(sceneId: string): boolean {
    return this.m_importingSceneIds.has(sceneId);
  }

  async importSkybox(sceneId: string, file: File): Promise<void> {
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!SKYBOX_IMPORT_EXTENSIONS.includes(extension as never)) {
      throw new Error(`Unsupported skybox format ".${extension}"`);
    }
    if (this.m_importingSceneIds.has(sceneId)) return;

    this.m_importingSceneIds.add(sceneId);
    this.onBeginSkyboxImport.emit({ sceneId });
    try {
      await this.m_projectModel.updateSkyboxTextureAsync(sceneId, file);
      this.m_importingSceneIds.delete(sceneId);
      this.refreshIfActive(sceneId);
      this.onSkyboxImportCompleted.emit({ sceneId });
    } catch (err) {
      this.m_importingSceneIds.delete(sceneId);
      const error = err instanceof Error ? err : new Error(String(err));
      this.onSkyboxImportFailed.emit({ sceneId, error });
      throw error;
    }
  }

  async clearSkybox(sceneId: string): Promise<void> {
    await this.m_projectModel.updateSkyboxTextureAsync(sceneId, null);
    this.refreshIfActive(sceneId);
  }

  private refreshIfActive(sceneId: string): void {
    if (this.m_projectModel.activeSceneId !== sceneId) return;
    this.m_skyboxModel.refreshSkybox(this.m_projectModel.getScene(sceneId));
  }
}

export const SKYBOX_IMPORT_MODEL = Symbol.for("Campfire.SkyboxImportModel");
